import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';

export const historicalPhotoCacheTable = 'checklist_photos';
export interface HistoricalPhotoScope { userId: string; companyId: string; checklistId: string }

export const historicalPhotoCacheKey = (scope: HistoricalPhotoScope, url: string): string => {
  const hash = Array.from(nacl.hash(naclUtil.decodeUTF8(url))).map(byte => byte.toString(16).padStart(2, '0')).join('');
  return `historical-photo:v1:${encodeURIComponent(scope.userId)}:${encodeURIComponent(scope.companyId)}:${encodeURIComponent(scope.checklistId)}:${hash}`;
};

export const materializeHistoricalPhoto = async (scope: HistoricalPhotoScope, url: string, deps: {
  read(id: string): Promise<{ id: string; base64?: string } | null>;
  download(): Promise<string>;
  onDownloadError?(error: unknown): void;
  revalidate(): Promise<boolean>;
  commit(row: { id: string; base64: string; created_at: number; updated_at: number }): Promise<number>;
}): Promise<boolean> => {
  if (!scope.userId || !scope.companyId || !scope.checklistId || !/^https?:\/\//.test(url)) return false;
  const id = historicalPhotoCacheKey(scope, url);
  const cached = await deps.read(id);
  if (cached) return cached.id === id && Boolean(cached.base64);
  if (!await deps.revalidate()) return false;
  let base64: string;
  try { base64 = await deps.download(); }
  catch (error) { deps.onDownloadError?.(error); return false; }
  if (!base64 || !await deps.revalidate()) return false;
  const inserted = await deps.commit({ id, base64, created_at: Date.now(), updated_at: Date.now() });
  const current = inserted === 1 ? null : await deps.read(id);
  return inserted === 1 || (current?.id === id && Boolean(current.base64));
};

/** Bytes permanecem em memória até a confirmação transacional do cache histórico. */
export const downloadHistoricalMediaBase64 = async (url: string, timeoutMs: number): Promise<string> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const bytes = new Uint8Array(await response.arrayBuffer());
    return naclUtil.encodeBase64(bytes);
  } finally { clearTimeout(timeoutId); }
};
